import { computed, ref } from 'vue';
import {
  BRANCHING_NODE_TYPES,
  NODE_TYPES,
  findStartNode,
} from '../helpers/sopFlow';

export function useSopSimulator(nodes, edges) {
  const state = ref({ active: false, currentNodeId: null, path: [] });

  // Per-node agent input captured during a run: checked action items + feedback.
  const runData = ref({});

  const ensureRunData = nodeId => {
    if (!runData.value[nodeId]) {
      runData.value[nodeId] = {
        checked: {},
        feedback: '',
        confirmed: false,
        variables: {},
      };
    }
  };

  const runDataFor = nodeId => {
    ensureRunData(nodeId);
    return runData.value[nodeId];
  };

  const currentNode = computed(
    () =>
      nodes.value.find(node => node.id === state.value.currentNodeId) || null
  );

  const edgeFrom = (sourceHandle = null) =>
    edges.value.find(
      edge =>
        edge.source === state.value.currentNodeId &&
        (edge.sourceHandle || null) === (sourceHandle || null)
    );

  const clearTraversal = () => {
    edges.value.forEach(edge => {
      if (edge.animated) edge.animated = false;
    });
  };

  const start = () => {
    const startNode = findStartNode(nodes.value);
    if (!startNode) return false;
    clearTraversal();
    runData.value = {};
    state.value = {
      active: true,
      currentNodeId: startNode.id,
      path: [startNode.id],
    };
    return true;
  };

  const stop = () => {
    clearTraversal();
    runData.value = {};
    state.value = { active: false, currentNodeId: null, path: [] };
  };

  const reset = () => start();

  // Resume a persisted run (operating mode): restore per-node input and the
  // traversal path instead of starting from the Start node.
  const hydrate = ({ path = [], nodes: nodeRunData = {} } = {}) => {
    const lastNodeId = path[path.length - 1];
    // Bail out (caller restarts) when the flow was edited since the run
    // began and the resume point no longer exists.
    if (!lastNodeId || !nodes.value.some(node => node.id === lastNodeId)) {
      return false;
    }
    clearTraversal();
    runData.value = nodeRunData;
    state.value = {
      active: true,
      currentNodeId: path[path.length - 1],
      path: [...path],
    };
    return true;
  };

  const advance = (branchId = null) => {
    const edge = edgeFrom(branchId);
    if (!edge) return false;
    edge.animated = true;
    state.value.currentNodeId = edge.target;
    state.value.path.push(edge.target);
    return true;
  };

  // Step back to the previous node (e.g. wrong branch picked). Per-node
  // input is kept so returning to a step doesn't lose what was entered.
  const back = () => {
    const { path } = state.value;
    if (path.length < 2) return false;
    const source = path[path.length - 2];
    const target = path[path.length - 1];
    const edge = edges.value.find(
      e => e.source === source && e.target === target && e.animated
    );
    if (edge) edge.animated = false;
    path.pop();
    state.value.currentNodeId = path[path.length - 1];
    return true;
  };

  const hasEdgeFor = (branchId = null) => Boolean(edgeFrom(branchId));

  const isComplete = computed(() => currentNode.value?.type === NODE_TYPES.END);
  const isBranching = computed(() =>
    BRANCHING_NODE_TYPES.includes(currentNode.value?.type)
  );
  const isDeadEnd = computed(() => {
    if (!currentNode.value || isComplete.value || isBranching.value) {
      return false;
    }
    return !hasEdgeFor(null);
  });

  return {
    state,
    currentNode,
    runData,
    runDataFor,
    start,
    stop,
    reset,
    hydrate,
    advance,
    back,
    hasEdgeFor,
    isComplete,
    isBranching,
    isDeadEnd,
  };
}
